<template>
  <div>
    <div class="page-toolbar">
      <div class="page-title">轮播图管理</div>
      <div class="toolbar-actions">
        <el-button type="primary" icon="el-icon-plus" @click="openCreateDialog">新增轮播</el-button>
        <el-button icon="el-icon-refresh" @click="loadBanners">刷新</el-button>
      </div>
    </div>

    <el-card class="page-card">
      <el-table :data="banners" v-loading="loading" stripe>
        <el-table-column type="index" width="56" label="#" />
        <el-table-column label="图片" width="120">
          <template slot-scope="{ row }">
            <el-image
              v-if="row.image"
              :src="row.image"
              :preview-src-list="[row.image]"
              fit="cover"
              class="banner-image"
            />
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" min-width="180" />
        <el-table-column prop="linkUrl" label="跳转链接" min-width="240" show-overflow-tooltip />
        <el-table-column prop="sortOrder" label="排序" width="90" />
        <el-table-column label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.enabled ? 'success' : 'info'">
              {{ row.enabled ? '启用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="updatedAt" label="更新时间" width="170" />
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="openEditDialog(row)">编辑</el-button>
            <el-button size="mini" :type="row.enabled ? 'warning' : 'success'" @click="toggleEnabled(row)">
              {{ row.enabled ? '停用' : '启用' }}
            </el-button>
            <el-button size="mini" type="danger" @click="removeBanner(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog :title="isEditing ? '编辑轮播' : '新增轮播'" :visible.sync="dialogVisible" width="560px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="标题" prop="title">
          <el-input v-model.trim="form.title" placeholder="选填，建议 12 字内" maxlength="40" show-word-limit />
        </el-form-item>

        <el-form-item label="图片地址" prop="image">
          <el-input v-model.trim="form.image" placeholder="可直接填写图片 URL，或使用下方本地上传" />
          <div class="image-actions">
            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              class="hidden-input"
              @change="handleImageSelect"
            >
            <el-button size="mini" :loading="uploading" @click="triggerImageSelect">本地上传</el-button>
            <span class="image-tip">上传后会自动回填 COS 图片地址</span>
          </div>
          <el-image
            v-if="form.image"
            :src="form.image"
            :preview-src-list="[form.image]"
            fit="cover"
            class="banner-preview"
          />
        </el-form-item>

        <el-form-item label="跳转链接" prop="linkUrl">
          <el-input v-model.trim="form.linkUrl" placeholder="可填写网络链接或小程序页面路径" />
        </el-form-item>

        <el-form-item label="排序" prop="sortOrder">
          <el-input-number v-model="form.sortOrder" :min="0" :max="9999" />
        </el-form-item>

        <el-form-item label="状态" prop="enabled">
          <el-switch v-model="form.enabled" active-text="启用" inactive-text="停用" />
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  createBanner,
  deleteBanner,
  fetchBanners,
  updateBanner,
  uploadAdminImage
} from '../../api/admin'
import { isAuthRedirectError } from '../../utils/auth'

const EMPTY_FORM = {
  id: '',
  title: '',
  image: '',
  linkUrl: '',
  sortOrder: 0,
  enabled: true
}

export default {
  name: 'BannerListView',
  data() {
    return {
      loading: false,
      saving: false,
      uploading: false,
      banners: [],
      dialogVisible: false,
      isEditing: false,
      form: { ...EMPTY_FORM },
      rules: {
        image: [
          { required: true, message: '请填写图片地址或上传图片', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.loadBanners()
  },
  methods: {
    async loadBanners() {
      this.loading = true
      try {
        const list = await fetchBanners()
        this.banners = Array.isArray(list) ? list : []
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '轮播图列表获取失败')
      } finally {
        this.loading = false
      }
    },

    openCreateDialog() {
      this.isEditing = false
      this.form = { ...EMPTY_FORM, sortOrder: this.banners.length * 10 + 10, enabled: true }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.clearValidate()
        if (this.$refs.fileInput) this.$refs.fileInput.value = ''
      })
    },

    openEditDialog(row) {
      this.isEditing = true
      this.form = {
        id: row.id || '',
        title: row.title || '',
        image: row.image || '',
        linkUrl: row.linkUrl || '',
        sortOrder: Number.isFinite(Number(row.sortOrder)) ? Number(row.sortOrder) : 0,
        enabled: !!row.enabled
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.clearValidate()
        if (this.$refs.fileInput) this.$refs.fileInput.value = ''
      })
    },

    triggerImageSelect() {
      if (this.uploading) return
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click()
      }
    },

    async handleImageSelect(event) {
      const files = event && event.target && event.target.files ? event.target.files : []
      const file = files && files[0] ? files[0] : null
      if (!file) return

      this.uploading = true
      try {
        const result = await uploadAdminImage(file, 'banner')
        const imageUrl = result && result.url ? result.url : ''
        if (!imageUrl) {
          throw new Error('上传成功但未返回图片地址')
        }
        this.form.image = imageUrl
        if (this.$refs.formRef) {
          this.$refs.formRef.validateField('image')
        }
        this.$message.success('图片上传成功')
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '图片上传失败')
      } finally {
        this.uploading = false
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = ''
        }
      }
    },

    async submitForm() {
      if (!this.$refs.formRef) return
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        this.saving = true
        try {
          const payload = {
            title: this.form.title || '',
            image: this.form.image,
            linkUrl: this.form.linkUrl || '',
            sortOrder: Number(this.form.sortOrder) || 0,
            enabled: !!this.form.enabled
          }
          if (this.isEditing) {
            await updateBanner(this.form.id, payload)
            this.$message.success('轮播图已更新')
          } else {
            await createBanner(payload)
            this.$message.success('轮播图已创建')
          }
          this.dialogVisible = false
          await this.loadBanners()
        } catch (error) {
          if (isAuthRedirectError(error)) return
          this.$message.error(error.message || '保存失败')
        } finally {
          this.saving = false
        }
      })
    },

    async toggleEnabled(row) {
      try {
        await updateBanner(row.id, {
          title: row.title || '',
          image: row.image || '',
          linkUrl: row.linkUrl || '',
          sortOrder: Number(row.sortOrder) || 0,
          enabled: !row.enabled
        })
        this.$message.success('状态已更新')
        await this.loadBanners()
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '状态更新失败')
      }
    },

    removeBanner(row) {
      this.$confirm('确认删除该轮播图吗？', '提示', { type: 'warning' })
        .then(async () => {
          await deleteBanner(row.id)
          this.$message.success('删除成功')
          await this.loadBanners()
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.toolbar-actions {
  display: flex;
  gap: 10px;
}

.banner-image {
  width: 84px;
  height: 48px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
}

.hidden-input {
  display: none;
}

.image-actions {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.image-tip {
  color: #909399;
  font-size: 12px;
}

.banner-preview {
  margin-top: 12px;
  width: 180px;
  height: 96px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
}
</style>
